import { Button, DialogContent, DialogTitle, Divider } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { ApiGatewaySubscription } from "../../../api/types/integrationsTypes";
import type { SubscriptionFormState } from "./ApiGatewaySubscriptionForm";
import ApiGatewaySubscriptionForm from "./ApiGatewaySubscriptionForm";

interface Props {
  subscription: ApiGatewaySubscription;
  onCancel: () => void;
  onSubmit: (updatedSubscription: ApiGatewaySubscription) => void;
}

const updateApiGatewaySubscription = withErrorHandling(adminApi.updateApiGatewaySubscription);

const EditSubscriptionDialog = ({ onCancel, onSubmit, subscription }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<SubscriptionFormState>({
    user: { userId: subscription.userId, username: subscription.username, email: subscription.email },
    description: subscription.description || "",
    type: subscription.type,
  });

  const isDirty = () => subscription.description?.trim() !== form.description.trim();

  const handleFormChange = (value: SubscriptionFormState) => setForm(value);

  const handleCancel = () => {
    onCancel();
  };

  const handleSubmit = async () => {
    if (!isDirty()) {
      return;
    }

    setSaving(true);
    const [updatedSubscription, error] = await updateApiGatewaySubscription(subscription.id, {
      description: form.description,
    });
    setSaving(false);

    if (error) {
      logError(error, "[EditApiGatewaySubscriptionDialog]");
      sendNotificationError(`Failed to update subscription for ${subscription.username}`);
      return;
    }

    sendNotification(`Subscription for ${subscription.username} successfully updated`);
    onSubmit(updatedSubscription);
  };

  return (
    <SmallDialog open onClose={handleCancel}>
      <DialogTitle>Edit Subscription</DialogTitle>
      <DialogCloseButton onClick={handleCancel} />
      <Divider />
      <DialogContent sx={(t) => ({ px: t.spacing(4) })}>
        <ApiGatewaySubscriptionForm
          users={form.user ? [form.user] : []}
          form={form}
          onChange={handleFormChange}
          disableUserSelection
          disableTypeSelection
        />
      </DialogContent>
      <Divider />
      <DialogButtons onCancel={handleCancel}>
        <Button variant="contained" color="primary" loading={saving} onClick={handleSubmit} disabled={!isDirty()}>
          Save
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default EditSubscriptionDialog;
